import React, { useState, useEffect, useMemo } from "react";
import {
  collection,
  getDocs,
  writeBatch,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import {
  Collections,
  PendingBrewingTransaction,
  Song,
  Artist,
} from "../types/type";
import { fetchAndSetData, mapDocToData } from "../api/fetch.api";

// 상태 타입 상수 정의
const TRANSACTION_STATUS = {
  COMPLETED: "completed" as const,
  FAILED: "failed" as const,
  PENDING: "pending" as const,
} satisfies Record<string, PendingBrewingTransaction["status"]>;
const fetchPendingTransactions = async (): Promise<
  PendingBrewingTransaction[]
> => {
  try {
    const snapshot = await getDocs(
      collection(db, Collections.PENDING_BREWING_TRANSACTION)
    );
    return snapshot.docs.map((doc) =>
      mapDocToData<PendingBrewingTransaction>(doc)
    );
  } catch (error) {
    console.error("Error fetching pending transactions: ", error);
    return [];
  }
};

const updateTransactionStatus = async (
  id: string,
  newStatus: PendingBrewingTransaction["status"],
  updateData: Partial<
    Pick<PendingBrewingTransaction, "error" | "model" | "songId" | "status">
  > = {}
): Promise<void> => {
  try {
    const transactionRef = doc(db, Collections.PENDING_BREWING_TRANSACTION, id);

    const finalUpdateData: Partial<PendingBrewingTransaction> = {
      status: newStatus,
      ...updateData,
    };

    await updateDoc(transactionRef, finalUpdateData);

    console.log(
      `[실행성공] Transaction ${id} 상태가 ${newStatus}로 업데이트되었습니다.`
    );
  } catch (error) {
    console.error("[에러] Transaction 업데이트 중 오류 발생: ", error);
    throw error;
  }
};

// 메인 컴포넌트
const PendingBrewingTransactionPage: React.FC = () => {
  const [transactions, setTransactions] = useState<PendingBrewingTransaction[]>(
    []
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [selectedSong, setSelectedSong] = useState<Song | null>(null);
  const [selectedTransaction, setSelectedTransaction] =
    useState<PendingBrewingTransaction | null>(null);
  const [message, setMessage] = useState<string>("");
  const [totalSongList, setTotalSongList] = useState<Song[]>([]);
  const [totalArtistList, setTotalArtistList] = useState<Artist[]>([]);

  // 곡 검색과 관련된 상태
  const [searchSongTerm, setSearchSongTerm] = useState<string>("");
  const [showSongSearch, setShowSongSearch] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<"artist" | "date">("artist");

  useEffect(() => {
    const loadInitialData = async () => {
      // 모든 트랜잭션 로드
      await fetchAndSetData(fetchPendingTransactions, setTransactions);

      // 모든 Song 목록 로드
      const songSnapshot = await getDocs(collection(db, Collections.SONG));
      const songs = songSnapshot.docs.map((doc) => mapDocToData<Song>(doc));
      setTotalSongList(songs);

      // 모든 Artist 목록 로드
      const artistSnapshot = await getDocs(collection(db, Collections.ARTIST));
      const artists = artistSnapshot.docs.map((doc) =>
        mapDocToData<Artist>(doc)
      );
      setTotalArtistList(artists);
    };
    loadInitialData();
  }, []);

  const handleTransactionSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSongSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchSongTerm(e.target.value);
  };

  const handleSongSelect = (song: Song) => {
    setSelectedSong(song);
    setShowSongSearch(false);
  };

  const sortedSongsByDate = useMemo(
    () =>
      totalSongList
        .map((song) => ({
          ...song,
          artistName:
            totalArtistList.find((artist) =>
              song.artistIdList.includes(artist.artistId)
            )?.artistName || song.artistName,
        }))
        .sort(
          (a, b) => (b.madeAt?.toDate?.() || 0) - (a.madeAt?.toDate?.() || 0)
        )
        .filter((song) =>
          song.title.toLowerCase().includes(searchSongTerm.toLowerCase())
        ),
    [totalSongList, totalArtistList, searchSongTerm]
  );

  // 필터링된 트랜잭션 목록 계산
  const filteredTransactions = transactions.filter((transaction) => {
    const matchesSearch =
      searchTerm === "" ||
      transaction.guideTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.id.toLowerCase().includes(searchTerm.toLowerCase());

    // 날짜 필터링
    const transactionDate = transaction.requestAt
      ?.toDate?.()
      ?.toISOString?.()
      ?.split("T")[0];
    const matchesDate = !selectedDate || transactionDate === selectedDate;

    return matchesSearch && matchesDate;
  });
  const songsByArtist = totalArtistList.reduce(
    (acc: { [key: string]: Song[] }, artist: Artist) => {
      acc[artist.artistId] = totalSongList.filter((song) =>
        song.artistIdList.includes(artist.artistId)
      );
      return acc;
    },
    {}
  );

  const handleStatusUpdate = async (
    transaction: PendingBrewingTransaction,
    newStatus: PendingBrewingTransaction["status"]
  ) => {
    try {
      //   if (newStatus === TRANSACTION_STATUS.COMPLETED && !selectedSong) {
      //     setMessage("완료 처리하기 전에 관련 곡을 선택해주세요.");
      //     return;
      //   }

      setMessage(`${transaction.id} 상태 변경 중...`);

      const updateData: Parameters<typeof updateTransactionStatus>[2] = {
        status: newStatus,
      };

      // completed 상태로 변경 시 선택된 곡 정보 추가
      //   if (newStatus === TRANSACTION_STATUS.COMPLETED && selectedSong) {
      //     updateData.model = selectedSong.isAI ? selectedSong.artist : "unknown";
      //     updateData.songId = selectedSong.songId;
      //   }

      // failed 상태로 변경 시 에러 메시지 추가
      if (newStatus === TRANSACTION_STATUS.FAILED) {
        updateData.error = "낮은 퀄리티";
      }

      await updateTransactionStatus(transaction.id, newStatus, updateData);

      // 목록 새로고침
      await fetchAndSetData(fetchPendingTransactions, setTransactions);

      setMessage(`상태가 성공적으로 ${newStatus}로 변경되었습니다.`);

      // 상태 초기화
      if (newStatus !== TRANSACTION_STATUS.PENDING) {
        setSelectedSong(null);
        setSelectedTransaction(null);
      }
    } catch (error: any) {
      setMessage(`오류 발생: ${error.message}`);
    }
  };

  return (
    <div style={styles.container}>
      <h1>Pending Brewing Transaction 관리</h1>

      <div style={styles.contentWrapper}>
        {/* 왼쪽: 트랜잭션 검색 및 목록 */}
        <div style={styles.transactionSection}>
          <div style={styles.searchSection}>
            <div style={styles.filterRow}>
              <input
                type="text"
                placeholder="트랜잭션 ID 또는 Guide Title로 검색"
                value={searchTerm}
                onChange={handleTransactionSearch}
                style={styles.input}
              />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={styles.dateInput}
              />
              <button
                onClick={() => setSelectedDate("")}
                style={styles.clearButton}
              >
                날짜 필터 초기화
              </button>
            </div>
          </div>

          {/* 트랜잭션 목록 */}
          <div style={styles.transactionList}>
            {filteredTransactions.map((transaction) => (
              <div
                key={transaction.id}
                style={{
                  ...styles.transactionItem,
                  ...(selectedTransaction?.id === transaction.id &&
                    styles.selectedItem),
                }}
                onClick={() => setSelectedTransaction(transaction)}
              >
                <div style={styles.transactionHeader}>
                  <span>ID: {transaction.id}</span>
                  <span>Status: {transaction.status}</span>
                </div>
                <div style={styles.transactionDetails}>
                  <p>Song Request ID: {transaction.songRequestId}</p>
                  <p>Model: {transaction.model}</p>
                  <p>Guide Title: {transaction.guideTitle}</p>
                  <p>
                    Status:{" "}
                    <span style={styles[transaction.status]}>
                      {transaction.status}
                    </span>
                  </p>
                  {transaction.error && (
                    <p style={styles.errorText}>Error: {transaction.error}</p>
                  )}
                </div>
                <div style={styles.actionButtons}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStatusUpdate(transaction, "completed");
                    }}
                    style={styles.completeButton}
                  >
                    Complete
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStatusUpdate(transaction, "failed");
                    }}
                    style={styles.failButton}
                  >
                    Fail
                  </button>
                </div>
              </div>
            ))}
          </div>

          {message && <p style={styles.message}>{message}</p>}
        </div>

        {/* 오른쪽: 곡 검색 및 결과 */}
        <div style={styles.songSearchSection}>
          <div style={styles.searchHeader}>
            <input
              type="text"
              placeholder="곡 검색"
              value={searchSongTerm}
              onChange={handleSongSearch}
              style={styles.input}
            />
            <button
              onClick={() =>
                setViewMode((prev) => (prev === "artist" ? "date" : "artist"))
              }
              style={styles.modeToggleButton}
            >
              {viewMode === "artist" ? "날짜순 보기" : "아티스트별 보기"}
            </button>
          </div>

          <div style={styles.songResults}>
            {viewMode === "artist" ? (
              Object.entries(songsByArtist).map(([artistId, songs]) => {
                const artist = totalArtistList.find(
                  (a) => a.artistId === artistId
                );
                const filteredSongs = songs.filter((song) =>
                  song.title
                    .toLowerCase()
                    .includes(searchSongTerm.toLowerCase())
                );

                if (!filteredSongs.length && searchSongTerm) return null;

                return (
                  <div key={artistId} style={styles.artistBlock}>
                    <h3>{artist?.artistName}</h3>
                    <ul style={styles.songList}>
                      {filteredSongs.map((song) => (
                        <li
                          key={song.songId}
                          onClick={() => handleSongSelect(song)}
                          style={{
                            ...styles.songItem,
                            ...(selectedSong?.songId === song.songId &&
                              styles.selectedSong),
                          }}
                        >
                          {song.title}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })
            ) : (
              <ul style={styles.songList}>
                {sortedSongsByDate.map((song) => (
                  <li
                    key={song.songId}
                    onClick={() => handleSongSelect(song)}
                    style={{
                      ...styles.songItem,
                      ...(selectedSong?.songId === song.songId &&
                        styles.selectedSong),
                    }}
                  >
                    {song.artistName} - {song.title}
                    <span style={styles.songDate}>
                      (
                      {song.madeAt?.toDate?.()?.toLocaleDateString() ||
                        "날짜 없음"}
                      )
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: "20px",
    maxWidth: "1400px",
    margin: "0 auto",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
  },
  contentWrapper: {
    display: "flex",
    gap: "20px",
    marginTop: "20px",
    flex: 1,
    minHeight: 0, // Important for flex child scrolling
  },
  transactionSection: {
    flex: "1",
    display: "flex",
    flexDirection: "column",
    minHeight: 0, // Important for flex child scrolling
  },
  songSearchSection: {
    flex: "1",
    borderLeft: "1px solid #ddd",
    paddingLeft: "20px",
    display: "flex",
    flexDirection: "column",
    minHeight: 0, // Important for flex child scrolling
  },
  searchSection: {
    marginBottom: "20px",
  },
  input: {
    width: "100%",
    padding: "10px",
    fontSize: "16px",
    borderRadius: "4px",
    border: "1px solid #ccc",
  },
  transactionList: {
    display: "grid",
    gap: "15px",
    overflowY: "auto",
    flex: 1,
    padding: "10px",
  },
  songResults: {
    overflowY: "auto",
    flex: 1,
    padding: "10px",
  },
  filterRow: {
    marginBottom: "10px",
  },
  dateInput: {
    padding: "10px",
    marginLeft: "10px",
    borderRadius: "4px",
    border: "1px solid #ccc",
  },
  clearButton: {
    padding: "10px",
    marginLeft: "10px",
    backgroundColor: "#6c757d",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
  transactionItem: {
    padding: "15px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    cursor: "pointer",
  },
  selectedItem: {
    borderColor: "#007bff",
    backgroundColor: "#f0f7ff",
  },
  transactionHeader: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "10px",
    fontWeight: "bold",
  },
  transactionDetails: {
    fontSize: "14px",
  },
  actionButtons: {
    display: "flex",
    gap: "10px",
    marginTop: "10px",
  },
  completeButton: {
    padding: "5px 15px",
    backgroundColor: "#28a745",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
  failButton: {
    padding: "5px 15px",
    backgroundColor: "#dc3545",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
  message: {
    marginTop: "20px",
    padding: "10px",
    backgroundColor: "#e9ecef",
    borderRadius: "4px",
  },
  songList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  songItem: {
    padding: "8px",
    cursor: "pointer",
    borderBottom: "1px solid #eee",
  },
  selectedSong: {
    backgroundColor: "#e3f2fd",
    fontWeight: "bold",
  },
  artistBlock: {
    marginBottom: "20px",
  },
  completed: {
    color: "#28a745",
    fontWeight: "bold",
  },
  failed: {
    color: "#dc3545",
    fontWeight: "bold",
  },
  pending: {
    color: "#ffc107",
    fontWeight: "bold",
  },
  errorText: {
    color: "#dc3545",
    fontSize: "0.9em",
    marginTop: "5px",
  },
  searchHeader: {
    display: "flex",
    gap: "10px",
    marginBottom: "20px",
  },
  modeToggleButton: {
    padding: "10px 15px",
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  songDate: {
    color: "#666",
    fontSize: "0.9em",
    marginLeft: "8px",
  },
};

export default PendingBrewingTransactionPage;
